import { environment } from '@environment'
import { inject } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { SetContextLink } from '@apollo/client/link/context'
import { HttpLink } from 'apollo-angular/http'
import { ErrorLink } from '@apollo/client/link/error'
import { ApolloLink, InMemoryCache } from '@apollo/client/core'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { any } from 'lodash/fp'
import { switchMap } from 'rxjs'
import { AuthCookieService } from '@ziptility/modules'
import { AuthService } from './services'
import { BvAlertService } from './bv-alert/alert.service'
import { StandardizedError } from './models/error.model'

export const apolloProvider = () => {
  const httpLink = inject(HttpLink)
  const authCookieService = inject(AuthCookieService)
  const authService = inject(AuthService)
  const alertService = inject(BvAlertService)

  const auth = new SetContextLink(async () => {
    const token = authCookieService.accessToken()
    return token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      : {}
  })

  const error = new ErrorLink(({ error: linkError, operation, forward }): any => {
    // Handle GraphQL errors
    if (CombinedGraphQLErrors.is(linkError)) {
      const operationDef = operation.query.definitions.find(
        (d): d is import('graphql').OperationDefinitionNode => d.kind === 'OperationDefinition',
      )
      const isQuery = operationDef?.operation === 'query'

      linkError.errors.forEach((err) => {
        // Skip AUTH_NOT_AUTHENTICATED as it's handled by token refresh
        if (err.extensions?.['code'] === 'AUTH_NOT_AUTHENTICATED') {
          return
        }

        // Display GraphQL error to user
        const errorMessage: string | StandardizedError = createGraphQLErrorMessage(err)
        alertService.error(errorMessage, isQuery)
      })
    }

    // Handle network errors with token refresh
    if (linkError instanceof HttpErrorResponse) {
      const { error: responseError } = linkError
      if (responseError && responseError.errors.length) {
        const hasInvalidTokenError = any(
          (e: any) => e.extensions && e.extensions.code === 'AUTH_NOT_AUTHENTICATED',
          responseError.errors,
        )
        if (hasInvalidTokenError) {
          return authService.refresh(authCookieService.refreshToken()).pipe(switchMap(() => forward(operation)))
        }
      }
    }
  })

  const uri = `${environment.apiBase}/graphql`
  const link = ApolloLink.from([error, auth, httpLink.create({ uri })])
  const cache = new InMemoryCache()

  return {
    link,
    cache,
    devtools: {
      enabled: !environment.production,
    },
  }
}

/**
 * Creates a user-facing error message from a GraphQL error
 */
function createGraphQLErrorMessage(error: any): string | StandardizedError {
  const message = error.message || 'An error occurred'

  // Check if error has structured details
  if (error.extensions && typeof error.extensions === 'object') {
    const { exception, ...otherExtensions } = error.extensions

    // If there are meaningful extensions beyond the exception, treat as details
    const extensionKeys = Object.keys(otherExtensions)
    if (extensionKeys.length > 0 && extensionKeys.some((key) => key !== 'code')) {
      return {
        message,
        details: [otherExtensions],
      }
    }
  }

  // Simple string message for basic errors
  return message
}
